/**
 * Phase 7 — Admin Gamification panel.
 *  - View usernames + real names + XP + level + achievements + streak
 *  - Reset inappropriate usernames
 *  - Toggle leaderboard opt-out
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, RotateCcw, EyeOff, Eye, Trophy, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const AdminGamificationPanel = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const r = await ax.get('/api/admin/gamification/users?limit=300');
      setUsers(r.data?.items || []);
    } catch (_e) { toast.error('Could not load gamification list'); }
  };
  useEffect(() => { load(); }, []);

  const resetUsername = async (email) => {
    const reason = window.prompt('Reset reason (shown in audit log):', 'inappropriate handle');
    if (reason === null) return;
    try {
      await ax.post('/api/admin/gamification/reset-username', { email, reason });
      toast.success('Username reset');
      load();
    } catch (_e) { toast.error('Reset failed'); }
  };

  const toggleOpt = async (email, optOut) => {
    try {
      await ax.post('/api/admin/gamification/leaderboard-opt-out', { email, opt_out: optOut });
      toast.success(`Leaderboard ${optOut ? 'hidden' : 'visible'} for ${email.split('@')[0]}`);
      load();
    } catch (_e) { toast.error('Toggle failed'); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.public_username || '').toLowerCase().includes(q);
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="admin-gamification-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-600" /> Gamification</h2>
          <p className="text-xs text-gray-500 mt-0.5">XP · Levels · Usernames · Achievements · Leaderboard opt-out.</p>
        </div>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, handle…"
            className="text-xs pl-8 pr-3 py-2 border rounded-full w-72 focus:outline-none focus:border-black"
            data-testid="gam-admin-search" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
            <tr>
              <th className="text-left py-2 pr-3">Student</th>
              <th className="text-left py-2 pr-3">Handle</th>
              <th className="text-right py-2 pr-3">XP</th>
              <th className="text-right py-2 pr-3">Level</th>
              <th className="text-right py-2 pr-3">Streak</th>
              <th className="text-right py-2 pr-3">Badges</th>
              <th className="text-right py-2 pr-3">LB</th>
              <th className="text-right py-2 pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.email} className="border-b last:border-b-0" data-testid={`gam-row-${u.email}`}>
                <td className="py-2 pr-3">
                  <div className="font-bold text-gray-900">{u.name || u.email.split('@')[0]}</div>
                  <div className="text-[10px] text-gray-500">{u.email}</div>
                </td>
                <td className="py-2 pr-3">{u.public_username ? <span className="font-mono">@{u.public_username}</span> : <span className="text-gray-400 italic">—</span>}</td>
                <td className="py-2 pr-3 text-right font-bold">{u.total_xp}</td>
                <td className="py-2 pr-3 text-right">{u.level} <span className="text-[10px] text-gray-500">{u.level_name}</span></td>
                <td className="py-2 pr-3 text-right">
                  <span className="inline-flex items-center gap-0.5"><Flame className="h-2.5 w-2.5 text-amber-500" />{u.streak_days}d</span>
                  {u.longest_streak > u.streak_days && <span className="text-[9px] text-gray-400"> /{u.longest_streak}</span>}
                </td>
                <td className="py-2 pr-3 text-right">{u.badges_count}</td>
                <td className="py-2 pr-3 text-right">
                  <button onClick={() => toggleOpt(u.email, !u.leaderboard_opt_out)} className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${u.leaderboard_opt_out ? 'bg-rose-50 text-rose-800 border border-rose-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}
                    data-testid={`gam-optout-${u.email}`}>
                    {u.leaderboard_opt_out ? <><EyeOff className="h-2.5 w-2.5" /> Hidden</> : <><Eye className="h-2.5 w-2.5" /> Visible</>}
                  </button>
                </td>
                <td className="py-2 pr-3 text-right">
                  <button onClick={() => resetUsername(u.email)} disabled={!u.public_username}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1 disabled:opacity-30"
                    data-testid={`gam-reset-${u.email}`}>
                    <RotateCcw className="h-2.5 w-2.5" /> Reset handle
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-gray-400 italic">No students match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminGamificationPanel;
